<template>
  <div ref="tabsRoot" class="content-owner-tabs" data-testid="content-owner-tabs">
    <Tabs id="content-owner-tabs" :selected-tab="selectedTab" @tab-click="onTabClick" @changed="(e: any) => onTabChange(e.tab.hash)">
      <!-- Summary/Description Tab (primary) -->
      <Tab id="summary" name="Summary" class="content-owner-tabs__tab">
        <div class="content-owner-tabs__summary">
          <div v-if="description" class="content-owner-tabs__summary-header">
            <div class="content-owner-tabs__summary-actions">
              <div class="content-owner-tabs__summary-actions-left">
                <Button
                  v-if="!summaryContentRef?.editMode"
                  type="link"
                  variant="gray"
                  icon="general.edited"
                  tooltip="Edit description"
                  data-testid="summary-edit-btn"
                  @click="summaryContentRef?.enterEditMode()"
                />
              </div>
              <Button
                v-if="!summaryContentRef?.editMode"
                class="content-owner-tabs__export-btn"
                type="solid"
                variant="blue"
                icon="general.generalExport"
                text="Export summary"
                data-testid="summary-export-btn"
                @click="openExportModal"
              />
            </div>
          </div>
          <SummaryContent ref="summaryContentRef" />
        </div>
      </Tab>

      <!-- Comments & Reactions Tab -->
      <Tab id="comments" :hidden="!engagementTabVisible" :name="engagementTabName" class="content-owner-tabs__tab">
        <div class="content-owner-tabs__engagement">
          <div v-if="!reactionsDisabled" class="content-owner-tabs__reactions-bar" :class="{ 'content-owner-tabs__reactions-bar--bordered': commentsEnabled }">
            <span class="reactions-label">Reactions</span>
            <Reactions :id="id" />
          </div>
          <Comments v-if="commentsEnabled" @addedComment="emit('addedComment')" />
        </div>
      </Tab>

      <!-- Analytics Tab -->
      <Tab id="analytics" :hidden="!analyticsTabEnabled" name="Analytics" class="content-owner-tabs__tab">
        <Analytics />
      </Tab>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { Tab, Tabs, Button } from '@/storybook';

import Comments from '@/modules/viewer/components/item_tabs/comments/Comments.vue';
import Analytics from '@/modules/viewer/components/item_tabs/analytics/Analytics.vue';
import Reactions from '@/modules/viewer/components/Reactions.vue';
import SummaryContent from '@/modules/viewer/components/SummaryContent.vue';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useTabNavigation } from '@/modules/viewer/composables/useTabNavigation';
import { useActiveTabListener } from '@/modules/viewer/composables/useActiveTab';
import { useEngagementTab } from '@/modules/viewer/composables/useEngagementTab';

const emit = defineEmits<{
  addedComment: [];
  navigationReady: [element: HTMLElement];
}>();

const tabsRoot = ref<HTMLElement | null>(null);

onMounted(async () => {
  await nextTick();
  const navigation = tabsRoot.value?.querySelector<HTMLElement>('.tabs-component-tabs');
  if (!navigation) {
    return;
  }

  navigation.dataset.testid = 'content-owner-tab-navigation';
  emit('navigationReady', navigation);
});

const store = useViewerItemStore();
const organizationStore = useOrganizationStore();
const { isCurrentItemOwner } = useUser();

const { id, commentsEnabled, reactionsDisabled, description } = storeToRefs(store);
const { trial } = storeToRefs(organizationStore);
const { openShareModalAction } = useItemActions();
const { engagementTabName, engagementTabVisible } = useEngagementTab();

const summaryContentRef = ref<InstanceType<typeof SummaryContent> | null>(null);

const openExportModal = () => {
  openShareModalAction({ modalProps: { selectedTab: '#export' } });
};

const analyticsTabEnabled = computed(() => {
  return isCurrentItemOwner.value && (trial?.value === undefined || trial?.value === false);
});

const availableTabs = computed(() => {
  const tabs: string[] = ['#summary'];
  if (engagementTabVisible.value) {
    tabs.push('#comments');
  }
  if (analyticsTabEnabled.value) {
    tabs.push('#analytics');
  }
  return tabs;
});

const { selectedTab, onTabClick, onTabChange, selectTab } = useTabNavigation(availableTabs, '#summary');

// Listen for external tab switch requests (from ButtonViews, etc.)
const { activeTabOverride } = useActiveTabListener();
watch(activeTabOverride, tab => {
  if (tab) {
    selectTab(tab);
    activeTabOverride.value = null;
  }
});
</script>

<style scoped>
.content-owner-tabs {
  @apply mt-2 sm:mt-4;
}

.content-owner-tabs__tab {
  @apply mt-3 sm:mt-4;
}

.content-owner-tabs__summary {
  @apply relative flex flex-col p-2 sm:p-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.content-owner-tabs__summary-header {
  @apply -m-2 p-2 mb-3 sm:mb-6 bg-white dark:bg-zinc-900 border-b border-slate-300 dark:border-zinc-600;
}

.content-owner-tabs__summary-actions {
  @apply flex items-center justify-between gap-1 sm:gap-2;
}

.content-owner-tabs__summary-actions-left {
  @apply flex items-center gap-1 sm:gap-2;
}

.content-owner-tabs__engagement {
  @apply relative flex flex-col p-3 sm:p-4 gap-3 sm:gap-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.content-owner-tabs__reactions-bar {
  @apply flex flex-wrap items-center gap-2 sm:gap-3;
}

.content-owner-tabs__reactions-bar--bordered {
  @apply pb-3 border-b border-slate-300 dark:border-zinc-600;
}

.reactions-label {
  @apply text-sm font-medium text-zinc-600 dark:text-zinc-400 mr-1;
}
</style>
